import { setTitleForIframe } from "./utils/setTitleForIframe";

export class IFrame {
  private el: HTMLElement;
  private abortController: AbortController;
  private iframes: NodeListOf<HTMLIFrameElement>;
  private viewportSpecificResizingEnabled = true;

  constructor(el: HTMLElement) {
    this.el = el;
    this.abortController = new AbortController();
    this.iframes = this.el.querySelectorAll<HTMLIFrameElement>("iframe");
  }

  init(): void {
    const signal = this.abortController.signal;

    document.addEventListener("viewportChange", this.handleViewportChange as EventListener, { signal });

    this.iframes.forEach((iframe) => {
      iframe.addEventListener("load", () => {
        setTitleForIframe(iframe);
      }, { signal });
    });

    this.initHeight();
    this.initIFrameResize();
  }

  private initIFrameResize(): void {
    const iFrameResizeFn = (window as any).iFrameResize as
      | ((options: any, target: Element | Element[] | NodeListOf<Element>) => void)
      | undefined;
    if (typeof iFrameResizeFn !== "function") return;

    this.iframes.forEach((iframe) => {
      iFrameResizeFn(
        {
          scrolling: iframe.getAttribute("scrolling") === "yes",
          resizedCallback: () => this.disableViewportSpecificResizing(),
        },
        iframe,
      );
    });
  }

  private disableViewportSpecificResizing(): void {
    if (this.viewportSpecificResizingEnabled) {
      this.viewportSpecificResizingEnabled = false;
    }
  }

  private handleViewportChange = (event: Event): void => {
    if (this.viewportSpecificResizingEnabled) {
      this.setHeight((event as CustomEvent).detail);
    }
  };

  private initHeight(): void {
    const htmlClasses = document.documentElement.className;
    let viewportSize = "small";

    if (htmlClasses.indexOf("medium") > 0) {
      viewportSize = "medium";
    } else if (htmlClasses.indexOf("large") > 0) {
      viewportSize = "large";
    }

    this.setHeight({ query: { name: viewportSize } });
  }

  private setHeight(data?: { query: { name: string } }): void {
    if (!data) return;

    let viewportSize = "small";
    if (data.query.name === "medium") {
      viewportSize = "medium";
    } else if (data.query.name === "large" || data.query.name === "xlarge") {
      viewportSize = "large";
    }

    this.iframes.forEach((iframe) => {
      const height = iframe.dataset[`height${viewportSize.charAt(0).toUpperCase() + viewportSize.slice(1)}`]
        ?? iframe.getAttribute(`data-height-${viewportSize}`);
      if (height !== undefined && height !== null) {
        iframe.style.height = height + "px";
      }
    });
  }

  destroy(): void {
    this.abortController.abort();
  }

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="iframe"]')
      .forEach((el) => {
        new IFrame(el).init();
      });
  }
}
